import "./Employee.scss";
import { TransitionGroup } from "components";
import { SmartTable } from "components";
import Loader from "components/layout/loader/Loader";
import useSmartTableSortOrder, { type TableType } from "context/SmartTableContext";
import { type Employee, EmployeeModel, EmployeeStatus, type Dealer } from "models";
import type { ModelInstance } from "models/Model";
import { Role } from "models/Role";
import { useCallback, useMemo, useState } from "react";
import type { ReactElement } from "react";
import Empty from "res/img/empty.svg?react";
import { Button, Input, Segment, Header, Select } from "semantic-ui-react";
import { hasAccess, useAuth, useUI } from "services";
import { USER_MSG } from "strings";
import AddModal from "./AddModal";
import { useT } from "i18n";
import EditDeleteModal from "./EditDeleteModal";

const AddEmployeeButton = ({
  isOpen,
  hasAddAccess,
  openAddModal,
  companyMappings,
  hasAccessToCompanyDropdown,
  setAddModal,
  isFromCompanyDetailRoute,
  dealerId,
  employees,
}: {
  isOpen: boolean;
  companyMappings: { value: string; text: string }[];
  hasAccessToCompanyDropdown: boolean;
  setAddModal: (addModal: boolean) => void;
  hasAddAccess: boolean;
  openAddModal: () => void;
  isFromCompanyDetailRoute: boolean;
  dealerId?: string;
  employees?: Employee[];
}) => {
  const t = useT();
  return (
    <>
      {hasAddAccess && (
        <Button
          primary
          icon={"plus"}
          className={
            isFromCompanyDetailRoute && employees?.length === 0
              ? "primary"
              : "main-action"
          }
          content={t("section.employee.addNewEmployee", "Add a New Employee")}
          onClick={openAddModal}
        />
      )}
      <AddModal
        isOpen={isOpen}
        companyMappings={companyMappings}
        onClose={() => {
          setAddModal(false);
        }}
        hasEditAccess={hasAddAccess}
        hasAccessToCompanyDropdown={hasAccessToCompanyDropdown}
        isFromCompanyDetailRoute={isFromCompanyDetailRoute}
        dealerId={dealerId}
      />
    </>
  );
};

const tableType: TableType = "employees";
const statuses: { [label: string]: string } = {
  Any: "",
  Active: EmployeeStatus.ACTIVE,
  Inactive: EmployeeStatus.INACTIVE,
};

export default function EmployeeList({
  showTOSStatus,
  dealerId,
  employees,
  dealers,
}: {
  showTOSStatus?: boolean;
  dealerId?: string;
  employees: Employee[];
  dealers: Dealer[];
}): ReactElement {
  const t = useT();
  const { user } = useAuth();
  const { devMode } = useUI();

  const [editModal, setEditModal] = useState<ModelInstance<Employee> | null>(null);

  const [addModal, setAddModal] = useState<boolean>(false);

  const { employeesTableState, updateTableFilteringState } = useSmartTableSortOrder();

  const query = employeesTableState.filters?.get("query") ?? "";
  const status = employeesTableState.filters?.get("status") ?? "";
  const filter = useCallback(
    (employee: Employee) => {
      const normalize = (str: string) => (str ? str.trim().toLowerCase() : "");
      const normedIncludes = (str1: string, str2: string) =>
        normalize(str1).includes(normalize(str2));
      return (
        (normedIncludes(employee.name, query) ||
          normedIncludes(employee.email, query) ||
          normedIncludes(employee.phone, query) ||
          normedIncludes(employee.role, query)) &&
        (!status ||
          status ===
            (employee.isActive ? EmployeeStatus.ACTIVE : EmployeeStatus.INACTIVE))
      );
    },
    [query, status],
  );

  const openAddModal = () => setAddModal(true);

  const companyMappings = useMemo(
    () =>
      (dealers || [])
        .map((dealer: Dealer) => ({
          key: dealer.id,
          value: dealer.id,
          text: `${dealer.name} (${dealer.rewardsId})`,
        }))
        .sort((a, b) =>
          a.text.toLocaleLowerCase().localeCompare(b.text.toLocaleLowerCase()),
        ),
    [dealers],
  );

  const convertedEmployees = useMemo(
    () =>
      employees.map((employee) => ({
        ...employee,
        dealerName:
          companyMappings.find((company) => company.value === employee.dealerId)
            ?.text ?? "",
      })),
    [employees, dealers],
  );

  const hasAccessToCompanyDropdown =
    devMode ||
    (!!user && (hasAccess(user, Role.BAI_ADMIN) || hasAccess(user, Role.LATHAM_ADMIN)));

  const hasEditAccess =
    user?.role !== Role.DESIGN &&
    user?.role !== Role.USER &&
    user?.role !== Role.MEASURE_LITE &&
    user?.role !== Role.SALES;

  const hasAddAccess =
    user?.role !== Role.DESIGN &&
    user?.role !== Role.SALES &&
    user?.role !== Role.USER &&
    user?.role !== Role.MEASURE_LITE;

  const isFromCompanyDetailRoute = !!dealerId;

  if (employees.length === 0) {
    return (
      <Loader
        topClass={isFromCompanyDetailRoute ? "loader-top" : ""}
        text={USER_MSG.EMPLOYEES_NOT_FOUND}
      >
        <AddEmployeeButton
          isOpen={addModal}
          companyMappings={companyMappings}
          hasAccessToCompanyDropdown={hasAccessToCompanyDropdown}
          setAddModal={setAddModal}
          hasAddAccess={hasAddAccess}
          openAddModal={openAddModal}
          isFromCompanyDetailRoute={true}
          dealerId={dealerId}
          employees={employees}
        />
      </Loader>
    );
  }

  return (
    <TransitionGroup isVisible>
      <EditDeleteModal
        companyMappings={companyMappings}
        employee={editModal}
        setEditModal={setEditModal}
        onClose={() => {
          setEditModal(null);
        }}
        hasEditAccess={hasEditAccess}
        hasDeleteAccess={hasAddAccess}
        hasAccessToCompanyDropdown={hasAccessToCompanyDropdown}
      />
      <Segment className={"employee-list pad"}>
        <Header size={"large"}>{t("section.employee.employees", "Employees")}</Header>
        <AddEmployeeButton
          isOpen={addModal}
          companyMappings={companyMappings}
          hasAccessToCompanyDropdown={hasAccessToCompanyDropdown}
          setAddModal={setAddModal}
          hasAddAccess={hasAddAccess}
          openAddModal={openAddModal}
          isFromCompanyDetailRoute={isFromCompanyDetailRoute}
          dealerId={dealerId}
          employees={employees}
        />
        <div className={"controls mbottom"}>
          <Input
            icon={"search"}
            placeholder={t(
              "section.employee.searchPlaceholder",
              "Name, Email or Phone...",
            )}
            onChange={(_, { value }) => {
              updateTableFilteringState("query", value, tableType);
            }}
            data-cy={"employee-filter-input"}
            value={query}
          />
          <Select
            placeholder={t("section.employee.statusPlaceholder", "Status")}
            options={Object.entries(statuses).map(([text, value]) => ({
              text,
              value,
            }))}
            onChange={(_, { value }) => {
              updateTableFilteringState("status", value as string, tableType);
            }}
            data-cy={"employee-status-select"}
            value={status}
          />
        </div>
        <SmartTable
          tableType={tableType}
          data={convertedEmployees}
          model={EmployeeModel}
          initialSortProp={"status"}
          filter={filter}
          excludeProps={showTOSStatus ? "" : " signedTOS "}
          transformProps={{
            createdAt: (val) => val.split("T")[0],
            isActive: (val) => (
              <div className={"chip " + (val === true ? "primary" : "")}>
                {val ? EmployeeStatus.ACTIVE : EmployeeStatus.INACTIVE}
              </div>
            ),
            signedTOS: (val) =>
              val
                ? t("section.employee.signed", "Signed")
                : t("section.employee.notSigned", "Not Signed"),
          }}
          onRowClick={(employee) => {
            setEditModal(EmployeeModel(employee));
          }}
          empty={<Empty />}
          className={"scroll"}
        />
      </Segment>
    </TransitionGroup>
  );
}
